import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Portal,
  Typography,
} from '@mui/material';
import { EmailGenerationProgressCard } from 'app/NewAIAvatarLayout/features/MiaChat/components/EmailGenerationProgressCard';
import type { useEmailTemplateGenerationProgressHost } from 'app/pages/EmailsGridPage/hooks/useEmailTemplateGenerationProgressHost';

type ProgressHost = ReturnType<typeof useEmailTemplateGenerationProgressHost>;

type EmailTemplateGenerationProgressOverlayProps = {
  host: ProgressHost;
};

/** Fixed progress card + optional details dialog — same component as EmailsGridPage. */
export function EmailTemplateGenerationProgressOverlay({
  host,
}: EmailTemplateGenerationProgressOverlayProps) {
  const {
    progressCardTitle,
    showGenerateProgressPopup,
    showSocketDetailsModal,
    setShowSocketDetailsModal,
    streamingDataForCard,
    currentSectionForCard,
    socketMessages,
    generationCompleted,
    progressFromSocket,
    isGenerateInProgress,
  } = host;

  return (
    <>
      {showGenerateProgressPopup ? (
        <Portal>
          <Box
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1400,
              minWidth: 520,
            }}
          >
            <EmailGenerationProgressCard
              isGeneratingEmail={showGenerateProgressPopup}
              streamingData={streamingDataForCard}
              currentSection={currentSectionForCard}
              title={progressCardTitle}
              onOpenEmailGenerationPopup={() => setShowSocketDetailsModal(true)}
            />
          </Box>
        </Portal>
      ) : null}

      <Dialog
        open={showSocketDetailsModal}
        onClose={() => setShowSocketDetailsModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ flexShrink: 0 }}>Template generation</DialogTitle>
        <DialogContent>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 1,
              minHeight: 120,
            }}
          >
            {(() => {
              const latest =
                socketMessages.length > 0
                  ? socketMessages[socketMessages.length - 1]
                  : null;
              const msg = latest?.data?.message;
              const total = msg?.total != null ? Number(msg.total) : null;
              const current = msg?.current != null ? Number(msg.current) : null;
              const progressPct =
                total != null && current != null && total > 0
                  ? Math.round((current / total) * 100)
                  : null;

              if (!latest && !generationCompleted && isGenerateInProgress) {
                return (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Generating templates…
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={
                        progressFromSocket.total > 0
                          ? Math.round(
                              (progressFromSocket.current /
                                progressFromSocket.total) *
                                100,
                            )
                          : 0
                      }
                      sx={{ mt: 1 }}
                    />
                  </Box>
                );
              }

              if (!latest) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    Waiting for generation updates…
                  </Typography>
                );
              }

              return (
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {latest.eventName?.replace(/_/g, ' ') ?? 'Update'}
                  </Typography>
                  {msg?.status != null ? (
                    <Typography variant="body2" color="text.secondary">
                      Status: {String(msg.status)}
                    </Typography>
                  ) : null}
                  {progressPct != null ? (
                    <Typography variant="body2" color="text.secondary">
                      Progress: {progressPct}%
                    </Typography>
                  ) : null}
                </Box>
              );
            })()}
          </Paper>
        </DialogContent>
      </Dialog>
    </>
  );
}
